import * as React from 'react';
import Papa from 'papaparse';
import BPromise from 'bluebird';
import { Loader } from 'Src/components/Loader/Loader';
import { superAdminGet, superAdminPost } from 'Src/helpers/service/superadmin/api';
import { getSuperAdminSchools } from 'Src/helpers/service/superadmin/schools';

// ─── CSV parsing ─────────────────────────────────────────────────────────────

function guessGender(val: string): string {
  const v = val.toLowerCase();
  if (v === 'boy' || v === 'male' || v === '1') return 'MALE';
  if (v === 'girl' || v === 'female' || v === '0') return 'FEMALE';
  return val.toUpperCase();
}

function parseBirthday(val: string): string | undefined {
  if (!val) return undefined;
  // Try DD-MM-YYYY or DD/MM/YYYY
  const parts = val.split(/[-/]/);
  if (parts.length === 3) {
    const [d, m, y] = parts;
    if (y.length === 4) return `${y}-${m.length < 2 ? '0' + m : m}-${d.length < 2 ? '0' + d : d}`;
  }
  return val;
}

interface StudentRow {
  firstName?: string;
  lastName?: string;
  gender?: string;
  birthday?: string;
  form?: string;
  house?: string;
  misId?: string;
  medicalInfo?: string;
  nextOfKin?: any[];
  formId?: string;
  houseId?: string;
}

interface ParseError {
  row: number;
  message: string;
}

function parseStudentsCSV(file: File): Promise<{ students: StudentRow[]; errors: ParseError[] }> {
  return new Promise((resolve, reject) => {
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: result => {
        const errors: ParseError[] = result.errors.map(e => ({ row: e.row || 0, message: e.message }));
        const rows = result.data as any[];
        const fields = result.meta.fields || [];

        // Map column names
        const get = (row: any, ...keys: string[]) => {
          for (const k of keys) {
            const found = fields.find(f => f.toLowerCase() === k.toLowerCase());
            if (found && row[found]) return row[found];
          }
          return undefined;
        };

        const students: StudentRow[] = rows
          .map(row => {
            const nokFields = Array.from({ length: 7 }, (_, i) => i + 1)
              .map(n => {
                const rel = get(row, `nextOfKin${n}.relationship`);
                if (!rel) return null;
                return {
                  relationship: rel,
                  firstName: get(row, `nextOfKin${n}.firstName`),
                  lastName: get(row, `nextOfKin${n}.lastName`),
                  email: get(row, `nextOfKin${n}.email`),
                  priority: get(row, `nextOfKin${n}.priority`) ? Number(get(row, `nextOfKin${n}.priority`)) : undefined,
                  phones: [
                    get(row, `nextOfKin${n}.phone1`),
                    get(row, `nextOfKin${n}.phone2`),
                    get(row, `nextOfKin${n}.phone3`)
                  ].filter(Boolean)
                };
              })
              .filter(Boolean);

            return {
              firstName: get(row, 'firstName'),
              lastName: get(row, 'lastName'),
              gender: get(row, 'gender') ? guessGender(get(row, 'gender')) : undefined,
              birthday: parseBirthday(get(row, 'date_of_birth', 'birthday')),
              form: get(row, 'form'),
              house: get(row, 'house'),
              misId: get(row, 'mis_id', 'misId'),
              medicalInfo: get(row, 'medical_info', 'medicalInfo'),
              nextOfKin: nokFields.length > 0 ? nokFields : undefined
            };
          })
          .filter(s => s.firstName || s.lastName);

        resolve({ students, errors });
      },
      error: err => reject(err)
    });
  });
}

function matchFormsHouses(
  students: StudentRow[],
  forms: Array<{ id: string; name: string }>,
  houses: Array<{ id: string; name: string }>
): { students: StudentRow[]; errors: ParseError[] } {
  const errors: ParseError[] = [];

  const mapped = students.map((s, i) => {
    const student = { ...s };
    if (s.form) {
      const form = forms.find(f => f.name.replace(/\s/g, '') === s.form!.replace(/\s/g, ''));
      if (form) {
        student.formId = form.id;
      } else {
        errors.push({ row: i, message: `Form "${s.form}" not found in school` });
      }
    }
    if (s.house) {
      const house = houses.find(h => h.name.replace(/\s/g, '') === s.house!.replace(/\s/g, ''));
      if (house) student.houseId = house.id;
    }
    return student;
  });

  return { students: mapped, errors };
}

// ─── Component ───────────────────────────────────────────────────────────────

interface Props {}

interface State {
  schoolSearch: string;
  schoolId: string;
  schoolName: string;
  schoolOptions: Array<{ id: string; name: string }>;
  isSearchingSchools: boolean;
  isLoadingSchoolData: boolean;
  forms: Array<{ id: string; name: string }>;
  houses: Array<{ id: string; name: string }>;
  students: StudentRow[];
  parseErrors: ParseError[];
  matchErrors: ParseError[];
  isUploading: boolean;
  uploadProgress: number;
  uploadTotal: number;
  uploadErrors: string[];
  successMessage: string;
  errorMessage: string;
}

export class SuperAdminImportStudents extends React.Component<Props, State> {
  schoolSearchTimer: any;

  constructor(props: Props) {
    super(props);
    this.state = {
      schoolSearch: '',
      schoolId: '',
      schoolName: '',
      schoolOptions: [],
      isSearchingSchools: false,
      isLoadingSchoolData: false,
      forms: [],
      houses: [],
      students: [],
      parseErrors: [],
      matchErrors: [],
      isUploading: false,
      uploadProgress: 0,
      uploadTotal: 0,
      uploadErrors: [],
      successMessage: '',
      errorMessage: ''
    };
  }

  onSchoolSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    this.setState({
      schoolSearch: val,
      schoolId: '',
      schoolName: '',
      students: [],
      parseErrors: [],
      matchErrors: [],
      successMessage: ''
    });
    clearTimeout(this.schoolSearchTimer);
    if (val.trim().length < 2) {
      this.setState({ schoolOptions: [] });
      return;
    }
    this.schoolSearchTimer = setTimeout(() => {
      this.setState({ isSearchingSchools: true });
      getSuperAdminSchools({ where: { name: { like: val, options: 'i' } }, limit: 10, skip: 0 })
        .then(schools =>
          this.setState({
            isSearchingSchools: false,
            schoolOptions: schools.map(s => ({ id: s.id, name: s.name || '' }))
          })
        )
        .catch(() => this.setState({ isSearchingSchools: false }));
    }, 300);
  };

  onSchoolSelect = (school: { id: string; name: string }) => {
    this.setState({
      schoolId: school.id,
      schoolName: school.name,
      schoolSearch: school.name,
      schoolOptions: [],
      isLoadingSchoolData: true,
      students: [],
      parseErrors: [],
      matchErrors: []
    });

    BPromise.all([
      superAdminGet(`/schools/${school.id}/forms`, { params: { filter: { limit: 1000, skip: 0 } } }),
      superAdminGet(`/schools/${school.id}/houses`, { params: { filter: { limit: 1000, skip: 0 } } })
    ])
      .then(([forms, houses]: any[]) => {
        this.setState({ isLoadingSchoolData: false, forms, houses });
      })
      .catch(() => this.setState({ isLoadingSchoolData: false, errorMessage: 'Could not load school forms/houses.' }));
  };

  onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;

    parseStudentsCSV(file).then(({ students, errors }) => {
      const { matched, matchErrors } = (() => {
        const result = matchFormsHouses(students, this.state.forms, this.state.houses);
        return { matched: result.students, matchErrors: result.errors };
      })();
      this.setState({ students: matched, parseErrors: errors, matchErrors, successMessage: '', uploadErrors: [] });
    });
  };

  retry = (fn: () => Promise<any>, times = 5): Promise<any> => {
    let p: Promise<any> = Promise.reject(new Error('initial'));
    for (let i = 0; i < times; i++) {
      p = p.catch(() => fn()).catch(() => new Promise((_, rej) => setTimeout(rej, 1000)));
    }
    return p;
  };

  onUpload = async () => {
    const { schoolId, students } = this.state;
    this.setState({
      isUploading: true,
      uploadProgress: 0,
      uploadTotal: students.length,
      uploadErrors: [],
      successMessage: '',
      errorMessage: ''
    });

    let progress = 0;
    const uploadErrors: string[] = [];

    for (const student of students) {
      try {
        const payload: any = {
          firstName: student.firstName,
          lastName: student.lastName,
          gender: student.gender
        };
        if (student.birthday) payload.birthday = student.birthday;
        if (student.formId) payload.formId = student.formId;
        if (student.houseId) payload.houseId = student.houseId;
        if (student.medicalInfo) payload.medicalInfo = student.medicalInfo;
        if (student.misId) payload.misId = student.misId;
        if (student.nextOfKin && student.nextOfKin.length > 0) payload.nextOfKin = student.nextOfKin;

        await this.retry(() => superAdminPost(`/schools/${schoolId}/students`, payload));
      } catch (err) {
        uploadErrors.push(`${student.firstName} ${student.lastName}: upload failed`);
      }
      progress++;
      this.setState({ uploadProgress: progress });
    }

    this.setState({
      isUploading: false,
      uploadErrors,
      successMessage:
        uploadErrors.length === 0
          ? `${students.length} student${students.length !== 1 ? 's' : ''} imported successfully.`
          : `Import finished with ${uploadErrors.length} error${uploadErrors.length !== 1 ? 's' : ''}.`
    });
  };

  render() {
    const {
      schoolSearch,
      schoolId,
      schoolName,
      schoolOptions,
      isSearchingSchools,
      isLoadingSchoolData,
      students,
      parseErrors,
      matchErrors,
      isUploading,
      uploadProgress,
      uploadTotal,
      uploadErrors,
      successMessage,
      errorMessage
    } = this.state;

    const allErrors = [...parseErrors, ...matchErrors];
    const canUpload = students.length > 0 && !isUploading;

    return (
      <div>
        <div className="mb-4">
          <h4 className="mb-1">Import students</h4>
          <div className="text-muted small">
            Upload a CSV with columns: <code>firstName</code>, <code>lastName</code>, <code>gender</code>,
            <code>date_of_birth</code>, <code>form</code>, <code>house</code>, <code>mis_id</code>,
            <code>medical_info</code>, <code>nextOfKin1.relationship</code>, etc.
          </div>
        </div>

        {/* School picker */}
        <div className="form-group" style={{ maxWidth: 420, position: 'relative' }}>
          <label className="small font-weight-bold">School</label>
          <input
            type="text"
            className="form-control form-control-sm"
            value={schoolSearch}
            onChange={this.onSchoolSearchChange}
            placeholder="Type to search school…"
          />
          {schoolId && <small className="text-success d-block mt-1">✓ {schoolName}</small>}
          {(schoolOptions.length > 0 || isSearchingSchools) && (
            <div
              style={{
                position: 'absolute',
                zIndex: 100,
                background: '#fff',
                border: '1px solid #ced4da',
                borderRadius: 4,
                width: '100%',
                maxHeight: 200,
                overflowY: 'auto'
              }}
            >
              {isSearchingSchools && <div className="p-2 text-muted small">Searching…</div>}
              {schoolOptions.map(s => (
                <div
                  key={s.id}
                  className="px-3 py-2 small"
                  style={{ cursor: 'pointer' }}
                  onMouseDown={() => this.onSchoolSelect(s)}
                >
                  {s.name}
                </div>
              ))}
            </div>
          )}
        </div>

        {isLoadingSchoolData && <Loader />}

        {/* File upload */}
        {schoolId && !isLoadingSchoolData && (
          <div className="form-group" style={{ maxWidth: 420 }}>
            <label className="small font-weight-bold">CSV file</label>
            <input type="file" className="form-control-file" accept=".csv" onChange={this.onFileChange} />
          </div>
        )}

        {/* Errors */}
        {allErrors.length > 0 && (
          <div className="alert alert-warning">
            <strong>
              {allErrors.length} issue{allErrors.length !== 1 ? 's' : ''} found:
            </strong>
            <ul className="mb-0 mt-1" style={{ maxHeight: 150, overflowY: 'auto' }}>
              {allErrors.map((e, i) => (
                <li key={i}>
                  Row {e.row}: {e.message}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Preview */}
        {students.length > 0 && (
          <div className="mb-3">
            <div className="text-muted small mb-2">
              Ready to import <strong>{students.length}</strong> student{students.length !== 1 ? 's' : ''}.
            </div>
            <div style={{ maxHeight: 200, overflowY: 'auto', border: '1px solid #dee2e6', borderRadius: 4 }}>
              <table className="table table-sm table-bordered mb-0">
                <thead className="thead-light">
                  <tr>
                    <th>Name</th>
                    <th>Gender</th>
                    <th>Birthday</th>
                    <th>Form</th>
                    <th>House</th>
                    <th>MIS ID</th>
                  </tr>
                </thead>
                <tbody>
                  {students.slice(0, 20).map((s, i) => (
                    <tr key={i}>
                      <td className="small">
                        {s.firstName} {s.lastName}
                      </td>
                      <td className="small">{s.gender}</td>
                      <td className="small">{s.birthday}</td>
                      <td className="small">
                        {s.formId ? (
                          <span className="text-success">{s.form}</span>
                        ) : (
                          <span className="text-muted">{s.form || '—'}</span>
                        )}
                      </td>
                      <td className="small">
                        {s.houseId ? (
                          <span className="text-success">{s.house}</span>
                        ) : (
                          <span className="text-muted">{s.house || '—'}</span>
                        )}
                      </td>
                      <td className="small">{s.misId || '—'}</td>
                    </tr>
                  ))}
                  {students.length > 20 && (
                    <tr>
                      <td colSpan={6} className="text-muted small text-center">
                        … and {students.length - 20} more
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Upload progress */}
        {isUploading && (
          <div className="mb-3">
            <div className="small mb-1">
              Uploading {uploadProgress} / {uploadTotal}…
            </div>
            <div className="progress" style={{ height: 8 }}>
              <div
                className="progress-bar"
                style={{ width: `${uploadTotal > 0 ? (uploadProgress / uploadTotal) * 100 : 0}%` }}
              />
            </div>
          </div>
        )}

        {uploadErrors.length > 0 && (
          <div className="alert alert-danger">
            <strong>Upload errors:</strong>
            <ul className="mb-0 mt-1">
              {uploadErrors.map((e, i) => (
                <li key={i}>{e}</li>
              ))}
            </ul>
          </div>
        )}

        {errorMessage && <div className="alert alert-danger">{errorMessage}</div>}
        {successMessage && <div className="alert alert-success">{successMessage}</div>}

        {canUpload && (
          <button className="btn btn-primary btn-sm" onClick={this.onUpload}>
            Import {students.length} student{students.length !== 1 ? 's' : ''}
          </button>
        )}
      </div>
    );
  }
}

